import { path, rrect, type Nub, type Span } from "./shape";

/*
 * Капли, которые сливаются и расходятся, — «goo» из макета v6 (`gooTarget`,
 * `gooNubs`, `gooLoop`). Здесь — сама физика: пружины на высоте и краях
 * капель, отростки между ними и толчок при разрыве. Физика только
 * раскладывает капли (место и размер каждой); материал у них — CSS, стекла
 * движка здесь больше нет (2 ноября: оно стоило кадров, а видно его не было).
 *
 * Числа — из макета без правок: жёсткость 320, вязкость 23 (у шапки 36),
 * отростки 1100/34 и мягко 300/20, толчок 85.
 */

const reduced = () =>
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export interface GooTarget {
  /** Высота капель. Меньше коробки — капли стоят посередине по вертикали. */
  h: number;
  /** Края капель по горизонтали, в точках от левого края коробки. */
  spans: Span[];
  /** Ближе этого зазора соседи сливаются. */
  merge: number;
  /** Вязкость пружин. */
  damping: number;
  /** Скругление прямоугольником вместо капель — для слитого многострочного поля. */
  radius: number | null;
}

interface NubState {
  conn: boolean[];
  nub: (Nub | null)[];
  pg: (number | undefined)[];
}

export class Goo {
  private x: number[] | null = null;
  private v: number[] = [];
  private sw = 0;
  private t = 0;
  private raf = 0;
  private d = "";
  private nb: NubState = { conn: [], nub: [], pg: [] };
  private target: GooTarget | null = null;

  constructor(
    private readonly box: HTMLElement,
    private readonly clip: HTMLElement,
    /** Откуда капли выходят при первом показе: одна капля, из которой рождаются остальные. */
    private readonly from?: Span[],
  ) {}

  aim(next: GooTarget) {
    this.target = next;
    this.kick();
  }

  kick() {
    if (!this.raf) this.raf = requestAnimationFrame(() => this.frame());
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Отростки: капли тянутся друг к другу перед слиянием и пружинят после разрыва. */
  private nubs(items: Span[], h: number, G: number, dt: number, rm: boolean) {
    const r = h / 2;
    const n = items.length - 1;
    const st = this.nb;
    const broke: number[] = [];
    let active = false;
    for (let i = 0; i < n; i++) {
      const gap = items[i + 1][0] - items[i][1];
      const conn = gap < G;
      const pg = st.pg[i];
      st.pg[i] = gap;
      const closing = pg != null && gap < pg - 1e-3;
      const Z = Math.max(2.5, Math.min(4, r * 0.16));
      if (st.conn[i] && !conn && !rm) {
        st.nub[i] = { u: 1, vu: 0, e: Math.max(0.5, gap / 2) };
        broke.push(i);
      }
      st.conn[i] = conn;
      if (conn) {
        st.nub[i] = null;
        continue;
      }
      if (!rm && closing && gap < G + Z) {
        let q = 1 - (gap - G) / Z;
        q = Math.max(0, Math.min(1, q));
        q = q * q * (3 - 2 * q);
        const prev = st.nub[i];
        st.nub[i] = { u: Math.max(q, prev ? prev.u : 0), vu: 0, e: Math.max(0.5, gap / 2) };
        active = true;
        continue;
      }
      const nb = st.nub[i];
      if (nb) {
        if (nb.u <= 0) nb.soft = true;
        const K = nb.soft ? 300 : 1100;
        const C = nb.soft ? 20 : 34;
        const ns = Math.max(1, Math.ceil(dt * 240));
        const hh = dt / ns;
        for (let s = 0; s < ns; s++) {
          nb.vu += (-nb.u * K - nb.vu * C) * hh;
          nb.u += nb.vu * hh;
        }
        if (nb.u < -0.55) {
          nb.u = -0.55;
          nb.vu = 0;
        }
        if (Math.abs(nb.u) < 0.004 && Math.abs(nb.vu) < 0.05) st.nub[i] = null;
        else active = true;
      }
    }
    return { nubs: st.nub.slice(), active, broke };
  }

  private frame() {
    this.raf = 0;
    const T = this.target;
    if (!T || !this.box.isConnected) return;
    const rm = reduced();
    const now = performance.now();
    const dt = Math.min(1 / 30, Math.max(1 / 240, (now - (this.t || now - 16.7)) / 1000));
    this.t = now;

    const tgt = [T.h, ...T.spans.flat()];
    if (!this.x || this.x.length !== tgt.length) {
      const start = this.from && this.from.length === T.spans.length ? [T.h, ...this.from.flat()] : tgt;
      this.x = start.slice();
      this.v = start.map(() => 0);
    }
    const x = this.x;
    const v = this.v;
    let moving = false;
    for (let i = 0; i < tgt.length; i++) {
      if (rm) {
        x[i] = tgt[i];
        v[i] = 0;
        continue;
      }
      const ns = Math.max(1, Math.ceil(dt * 240));
      const hh = dt / ns;
      for (let s = 0; s < ns; s++) {
        v[i] += ((tgt[i] - x[i]) * 320 - v[i] * T.damping) * hh;
        x[i] += v[i] * hh;
      }
      if (Math.abs(tgt[i] - x[i]) > 0.2 || Math.abs(v[i]) > 0.2) moving = true;
      else {
        x[i] = tgt[i];
        v[i] = 0;
      }
    }

    // Быстрое движение раздувает капли — так жидкость отзывается на рывок.
    let vmax = 0;
    for (let i = 1; i < v.length; i++) vmax = Math.max(vmax, Math.abs(v[i]));
    this.sw += (Math.min(9, vmax * 0.1) - this.sw) * Math.min(1, dt * 12);
    if (this.sw < 0.02) this.sw = 0;

    const h = x[0] + this.sw;
    const items: Span[] = [];
    for (let i = 1; i < x.length; i += 2) items.push([x[i], x[i + 1]]);
    const H = this.box.offsetHeight;
    const oy = (H - h) / 2;
    const NB = this.nubs(items, h, T.merge, dt, rm);
    if (NB.active || this.sw > 0) moving = true;
    if (NB.broke.length && !rm) {
      const I = 85;
      NB.broke.forEach((p) => {
        const a = 1 + p * 2;
        const b = 1 + (p + 1) * 2;
        v[a] -= I;
        v[a + 1] -= I;
        v[b] += I;
        v[b + 1] += I;
      });
      moving = true;
    }

    // Слитое многострочное поле — один прямоугольник от первой капли до последней.
    const shape: Span[] = T.radius != null ? [[items[0][0], items[items.length - 1][1]]] : items;
    const d = T.radius != null ? rrect(shape, h, oy, T.radius) : path(items, h, T.merge, oy, NB.nubs, 0.93, 1.5);
    if (d !== this.d) {
      this.d = d;
      const bh = x[0];
      const boy = (H - bh) / 2;
      this.clip.querySelectorAll<HTMLElement>(":scope > [data-goo-item]").forEach((it) => {
        const r = items[Number(it.dataset.gooItem)];
        if (!r) return;
        it.style.left = r[0].toFixed(1) + "px";
        it.style.width = (r[1] - r[0]).toFixed(1) + "px";
        it.style.height = bh.toFixed(1) + "px";
        it.style.top = boy.toFixed(1) + "px";
      });
    }
    if (moving) this.raf = requestAnimationFrame(() => this.frame());
    else this.t = 0;
  }
}
